import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaCheckCircle, FaHandshake, FaHospital, FaPaperPlane } from "react-icons/fa";
import { getApiUrl } from "../utils/config";
import { getActionErrorMessage } from "../utils/errorHandler";
import { useToast } from "../utils/toastContext";
import clinExpertLogo from "../ClinExpert_logo.png";
import "../styles/LoginForm.css";
import "../styles/OrganizationPages.css";
import AuthBackButton from "./AuthBackButton";

const INITIAL_FORM = {
  organization_name: "",
  organization_type: "",
  country: "",
  city: "",
  address: "",
  website: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  role: "",
  message: "",
};

const ROLE_OPTIONS = [
  "Dean / Academic Lead",
  "Program Director",
  "Clinical Education Coordinator",
  "Preceptor Lead",
  "Operations / Administration",
  "IT / Digital Transformation",
  "Other",
];

const JoinOrganizationPage = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(null);

  const isSubmitDisabled = useMemo(() => {
    return (
      isSubmitting ||
      !formData.organization_name.trim() ||
      !formData.first_name.trim() ||
      !formData.last_name.trim() ||
      !formData.email.trim() ||
      !formData.role.trim()
    );
  }, [formData, isSubmitting]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const response = await axios.post(getApiUrl("/organization/apply"), formData, {
        headers: { "Content-Type": "application/json" },
      });

      setSubmitted(response.data || {});
      showSuccess("Application submitted successfully.");
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        getActionErrorMessage(err, "submit organization application");
      setError(message);
      showError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="login-container">
        <AuthBackButton />
        <div className="login-shell join-shell">
          <section className="login-brand-panel">
            <img src={clinExpertLogo} alt="ClinExpert Logo" className="logo dual-logo" />
            <h1 className="login-title">Application Received</h1>
            <p className="login-subtitle">
              Thank you for your interest in joining the ClinExpert network.
            </p>
          </section>

          <section className="login-form-panel join-form-panel success-panel">
            <div className="success-icon-wrap" aria-hidden="true">
              <FaCheckCircle />
            </div>
            <h2 className="login-form-title">Submission Successful</h2>
            <p className="login-form-caption success-caption">
              Your organization application was submitted successfully. Our team will contact you soon.
            </p>
            {submitted.application_id ? (
              <p className="application-id-pill">Application ID: {submitted.application_id}</p>
            ) : null}
            <div className="success-actions">
              <button type="button" className="login-button" onClick={() => navigate("/")}>
                Back to Sign In
              </button>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <AuthBackButton />
      <div className="login-shell join-shell">
        <section className="login-brand-panel">
          <img src={clinExpertLogo} alt="ClinExpert Logo" className="logo dual-logo" />
          <h1 className="login-title">Join ClinExpert</h1>
          <p className="login-subtitle">
            Partner with ClinExpert to streamline clinical education, scheduling, and outcomes.
          </p>
          <div className="login-feature-list">
            <div className="login-feature-item">
              <FaHandshake />
              <span>Built for institutional collaboration</span>
            </div>
            <div className="login-feature-item">
              <FaHospital />
              <span>Supports hospitals, universities, and training centers</span>
            </div>
          </div>
        </section>

        <section className="login-form-panel join-form-panel">
          <h2 className="login-form-title">Organization Application</h2>
          <p className="login-form-caption">Please provide your institution and contact details.</p>

          <form onSubmit={handleSubmit} className="join-form-grid">
            <h3 className="join-section-title">Organization Details</h3>
            <div className="join-grid-2">
              <label className="join-field">
                <span>Organization Name *</span>
                <input
                  value={formData.organization_name}
                  onChange={(e) => handleChange("organization_name", e.target.value)}
                  required
                />
              </label>
              <label className="join-field">
                <span>Organization Type</span>
                <input
                  value={formData.organization_type}
                  onChange={(e) => handleChange("organization_type", e.target.value)}
                  placeholder="University, Hospital, Training Center..."
                />
              </label>
              <label className="join-field">
                <span>Country</span>
                <input value={formData.country} onChange={(e) => handleChange("country", e.target.value)} />
              </label>
              <label className="join-field">
                <span>City</span>
                <input value={formData.city} onChange={(e) => handleChange("city", e.target.value)} />
              </label>
            </div>
            <label className="join-field">
              <span>Address</span>
              <input value={formData.address} onChange={(e) => handleChange("address", e.target.value)} />
            </label>
            <label className="join-field">
              <span>Website</span>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => handleChange("website", e.target.value)}
                placeholder="https://your-org.example"
              />
            </label>

            <h3 className="join-section-title">Registrant Details</h3>
            <div className="join-grid-2">
              <label className="join-field">
                <span>First Name *</span>
                <input
                  value={formData.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  required
                />
              </label>
              <label className="join-field">
                <span>Last Name *</span>
                <input
                  value={formData.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  required
                />
              </label>
              <label className="join-field">
                <span>Email *</span>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                />
              </label>
              <label className="join-field">
                <span>Phone</span>
                <input value={formData.phone} onChange={(e) => handleChange("phone", e.target.value)} />
              </label>
            </div>

            <label className="join-field">
              <span>Role in Organization *</span>
              <select value={formData.role} onChange={(e) => handleChange("role", e.target.value)} required>
                <option value="">Select your role</option>
                {ROLE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <label className="join-field">
              <span>Optional Message</span>
              <textarea
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
                rows={4}
                placeholder="Tell us about your training needs or implementation goals."
              />
            </label>

            {error ? <p className="login-inline-status">{error}</p> : null}

            <button type="submit" className="login-button join-submit-button" disabled={isSubmitDisabled}>
              <FaPaperPlane />
              <span>{isSubmitting ? "Submitting..." : "Submit Application"}</span>
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default JoinOrganizationPage;
